import { useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { Toaster } from 'sonner'

import { Navigation } from '@/components/Navigation'
import { HomePage } from '@/pages/HomePage'
import { LoginPage } from '@/pages/LoginPage'
import { RegisterPage } from '@/pages/RegisterPage'
import { AuthCallbackPage } from '@/pages/AuthCallbackPage'
import { MagicLinkCallbackPage } from '@/pages/MagicLinkCallbackPage'
import { DashboardPage } from '@/pages/DashboardPage'
import { ScanHistoryPage } from '@/pages/ScanHistoryPage'
import { ScanResultsPage } from '@/pages/ScanResultsPage'
import { StartScanPage } from '@/pages/StartScanPage'
import { ExamplesPage } from '@/pages/ExamplesPage'

import { useAuthStore } from '@/stores/authStore'
import { useUIStore } from '@/stores/uiStore'
import { useWebSocket } from '@/hooks/useWebSocket'
import { cn } from '@/utils/cn'

// Protected Route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore()

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

// Layout wrapper for authenticated pages
function AppLayout({ children }: { children: React.ReactNode }) {
  const { sidebarOpen } = useUIStore()

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Navigation />
      <main className={cn(
        'lg:pl-72 transition-all duration-200',
        !sidebarOpen && 'lg:pl-16'
      )}>
        <div className="py-6 lg:py-8 px-4 sm:px-6 lg:px-8">
          {children}
        </div>
      </main>
    </div>
  )
}

function App() {
  const { isAuthenticated, user } = useAuthStore()
  const { theme } = useUIStore()

  // Initialize WebSocket connection for authenticated users
  useWebSocket()

  // Apply theme on app start
  useEffect(() => {
    const root = document.documentElement
    root.classList.remove('light', 'dark')

    if (theme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
      root.classList.add(systemTheme)
    } else {
      root.classList.add(theme)
    }
  }, [theme])

  // Listen for system theme changes
  useEffect(() => {
    if (theme === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
      const handleChange = () => {
        const root = document.documentElement
        root.classList.remove('light', 'dark')
        root.classList.add(mediaQuery.matches ? 'dark' : 'light')
      }

      mediaQuery.addEventListener('change', handleChange)
      return () => mediaQuery.removeEventListener('change', handleChange)
    }
  }, [theme])

  // Set document title based on user state
  useEffect(() => {
    if (isAuthenticated && user) {
      document.title = `RiskLens - ${user.username || user.email}`
    } else {
      document.title = 'RiskLens - Security Scanning Platform'
    }
  }, [isAuthenticated, user])

  return (
    <Router>
      <div className="min-h-screen bg-background text-foreground">
        <Routes>
          {/* Public routes */}
          <Route
            path="/login"
            element={
              isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginPage />
            }
          />

          <Route
            path="/register"
            element={
              isAuthenticated ? <Navigate to="/dashboard" replace /> : <RegisterPage />
            }
          />

          <Route
            path="/auth/callback"
            element={<AuthCallbackPage />}
          />

          <Route
            path="/auth/magic-link/callback"
            element={<MagicLinkCallbackPage />}
          />

          <Route
            path="/examples"
            element={<ExamplesPage />}
          />

          {/* Protected routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <DashboardPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/history"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <ScanHistoryPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/scan/:scanId"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <ScanResultsPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/start-scan"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <StartScanPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          {/* Home route - accessible to all but shows different content */}
          <Route
            path="/"
            element={
              isAuthenticated ? (
                <AppLayout>
                  <HomePage />
                </AppLayout>
              ) : (
                <HomePage />
              )
            }
          />

          {/* Catch-all redirect */}
          <Route
            path="*"
            element={
              <Navigate
                to={isAuthenticated ? "/dashboard" : "/"}
                replace
              />
            }
          />
        </Routes>

        {/* Toast notifications */}
        <Toaster
          position="top-right"
          expand={true}
          richColors
          closeButton
          toastOptions={{
            duration: 5000,
            className: 'toast',
          }}
        />
      </div>
    </Router>
  )
}

export default App
